import {ReactNode} from 'react';
import ReactDOM from 'react-dom/client';
import {ulid} from 'ulid';
import {callInterface, commonCallInterface} from '@shared/webviewUtils';

interface WebviewEventListener {
  (webviewResponse: any): void;
}

const INTERFACE_NAME = 'HMGOneAppInterface';

const WEBVIEW_RESULT_FUNCTION_NAME = 'appCallback_cc' as const;

const webviewEvent = (function (global) {
  let listeners: Array<WebviewEventListener> = [];

  function addListener(listener: WebviewEventListener) {
    listeners.push(listener);
  }

  function removeListener(listener: WebviewEventListener) {
    listeners = listeners.filter((l) => l !== listener);
  }

  global[WEBVIEW_RESULT_FUNCTION_NAME] = function (jsonString) {
    let response: object;
    try {
      response = JSON.parse(jsonString);
    } catch (e) {
      // eslint-disable-next-line no-control-regex
      response = JSON.parse(jsonString.replace(/[\x00-\x1F\x7F]/g, ''));
    }
    listeners.forEach((listener) => listener(response));
  };

  return {
    addListener,
    removeListener,
  };
})(window);

function oneAppCallInterface<T>(functionName: string, params?: T) {
  callInterface(INTERFACE_NAME, functionName, params);
}
export interface getAuthTokenProps {
  authToken: string;
  idToken: string;
  nonCcsToken: string;
}
export interface GetAuthTokenResponse {
  uid: string;
  authToken: string;
  idToken: string;
  nonCcsToken: string;
}

const GET_AUTH_TOKEN_REQUEST_FUNCTION_NAME = 'getAuthToken' as const;

let getAuthTokenPromise: null | Promise<getAuthTokenProps> = null;

const GET_AUTH_TOKEN_TIMEOUT = 3000;
const GET_AUTH_TOKEN_MAX_TRY = 3;

/**
 * 앱에게 accessToken 을 요청
 * 이 동작은 아래 조건을 모두 만족해야함 (앱과 협의 완료)
 * 1. 이 요청은 api 호출시마다 발생 (앱을 accessToken 저장소로 사용)
 *     - 사유: 토큰 관리 주체를 한곳(앱)으로 정의함. 여러곳에서 관리하는 경우 토큰 갱신 시 서로 동기화를 해야함. 또한 앱에서 토큰이 바뀌었을때 화면은 알 방법이 없음
 * 2. 이 요청은 반드시 사용가능한 accessToken 을 반환함 (우리가 검증하지 않음)
 *     - 사유: 이 토큰이 유효함을 웹에서 검증하면, 위와 같은 이유로 관리주체가 2곳이 되야함
 */
function getAuthToken() {
  if (getAuthTokenPromise) {
    // 다중 토큰 요청시 1건만 가능하게끔
    return getAuthTokenPromise;
  }

  function tryAuth() {
    let getAuthTokenTimeoutId: null | ReturnType<typeof setTimeout> = null;

    return new Promise<getAuthTokenProps>((resolve, reject) => {
      const uid = ulid();
      getAuthTokenTimeoutId = setTimeout(() => {
        webviewEvent.removeListener(listener);
        getAuthTokenTimeoutId = null;
        reject();
      }, GET_AUTH_TOKEN_TIMEOUT);

      function listener(response: GetAuthTokenResponse) {
        if (response.uid !== uid) {
          return;
        }
        if (getAuthTokenTimeoutId) {
          clearTimeout(getAuthTokenTimeoutId);
          getAuthTokenTimeoutId = null;
        }
        webviewEvent.removeListener(listener);
        /**
         * ToDo ToDo 앱 인터페이스 수정 후 string 제거 적용 필요
         */
        resolve({authToken: response.authToken, idToken: response.idToken, nonCcsToken: response.nonCcsToken});
      }

      webviewEvent.addListener(listener);
      oneAppCallInterface(GET_AUTH_TOKEN_REQUEST_FUNCTION_NAME, {
        uid,
        resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
      });
    });
  }

  async function tryWithRetry(retry: number) {
    try {
      return await tryAuth();
    } catch (e) {
      if (retry > 1) {
        return tryWithRetry(retry - 1);
      }
      closeContent(); // 이 경우 복원 불가임. 웹뷰 닫아버림
      throw new Error();
    }
  }

  getAuthTokenPromise = tryWithRetry(GET_AUTH_TOKEN_MAX_TRY).finally(() => {
    getAuthTokenPromise = null;
  });

  return getAuthTokenPromise;
}

export interface OpenBottomSheetWebViewFactory<R> {
  (close: (value: R) => void): ReactNode;
}

export interface OpenBottomSheetWebViewProps {
  isCancellable?: boolean;
}

export interface PopupWindowBody extends HTMLBodyElement {
  popupWindow: Window;
}

export interface PrepareWindowPresentationFactory<R> {
  (close: (value: R) => void): ReactNode;
}

export interface PrepareWindowPresentationProps {
  type: 'BOTTOM_SHEET' | 'MODAL';
  isCancellable?: boolean;
  isNavigationBar?: boolean; // BOTTOM_SHEET 타입일 경우 모두 무시 (2025년 9월 배포 추가)
  isToolBar?: boolean; // BOTTOM_SHEET 타입일 경우 모두 무시 (2025년 9월 배포 추가)
}

const PREPARE_WINDOW_PRESENTATION_FUNCTION_NAME = 'prepareWindowOpenPresentation' as const;
const PREPARE_WINDOW_PRESENTATION_FUNCTION_PREFIX = 'appCallback_prepareWindowPresentation_' as const;

export const BOTTOM_SHEET_CONTAINER = 'bottom-sheet-container';
export const BOTTOM_SHEET_READY_FUNCTION_PREFIX = 'onBottomSheetReady_';
export const BOTTOM_SHEET_DESTROY_FUNCTION_PREFIX = 'onBottomSheetDestroy_';
export const BOTTOM_SHEET_CLOSE_FUNCTION_PREFIX = 'onBottomSheetClose_';

async function openBottomSheetWebView<R>(
  factory: PrepareWindowPresentationFactory<R>,
  props: PrepareWindowPresentationProps = {type: 'BOTTOM_SHEET', isCancellable: true}
): Promise<R | false> {
  const uid = ulid();
  const readyFuncName = `${PREPARE_WINDOW_PRESENTATION_FUNCTION_PREFIX}${uid}`;

  await new Promise((resolve) => {
    (window as any)[readyFuncName] = resolve;
    // openBottomSheetWebView 는 react/style 등 프로젝트 레벨에 의존성이 있어 이곳에 구현. 단, 인터페이스는 Common 을 사용
    commonCallInterface(PREPARE_WINDOW_PRESENTATION_FUNCTION_NAME, {
      readyFuncName,
      ...props,
    });
  });

  delete (window as any)[readyFuncName];

  return new Promise((resolve) => {
    // 바텀시트 URL을 동일하게 사용하여 앱에서 HTML을 캐시할 수 있도록 함
    // 쿼리 스트링이 아니라 해시를 통해 고유번호 전달함.
    // Todo: routes 처리 필요 by EunA(2025.02.27)
    const popup = window.open(`/bottom-sheet/blank#${uid}`) as Window;

    if (!popup) {
      return;
    }

    let popupRoot: ReactDOM.Root;
    const bottomSheetReadyFuncName = `${BOTTOM_SHEET_READY_FUNCTION_PREFIX}#${uid}`;
    const bottomSheetDestroyFuncName = `${BOTTOM_SHEET_DESTROY_FUNCTION_PREFIX}#${uid}`;
    const bottomSheetCloseFuncName = `${BOTTOM_SHEET_CLOSE_FUNCTION_PREFIX}#${uid}`;

    (window as any)[bottomSheetReadyFuncName] = bottomSheetReady;
    (window as any)[bottomSheetDestroyFuncName] = destroyReact;
    (window as any)[bottomSheetCloseFuncName] = closePopup;

    function bottomSheetReady() {
      // 여기서 렌더링하는 컴포넌트에서 window를 참조하면 렉시컬 스코프에 의해 바닥 페이지의 window를 참조하므로
      // 바텀시트의 높이를 앱으로 전달할 때 제대로 컴포넌트가 마운트된 팝업의 window를 쓰게 하기 위해
      // 팝업의 엘리먼트에 팝업의 window 객체를 달아둔다.
      // setBottomSheetSize 앱 인터페이스에서 사용할 것임.
      const attachElement = popup.document.body as PopupWindowBody;
      attachElement.popupWindow = popup;

      popupRoot = ReactDOM.createRoot(attachElement);
      popupRoot.render(
        factory((value: R) => {
          closePopup(value);
        })
      );
    }

    function destroyReact() {
      if (popupRoot) {
        // 바텀시트 닫혔을 때 umount하여 리소스를 정리한다.
        popupRoot.unmount();
      }
    }

    function closePopup(value: R | false) {
      destroyReact();

      delete (window as any)[bottomSheetReadyFuncName];
      delete (window as any)[bottomSheetDestroyFuncName];
      delete (window as any)[bottomSheetCloseFuncName];

      resolve(value);
      popup?.close();
    }
  });
}

async function openBottomSheetWebViewUrl<R>(
  url: string,
  data?: unknown,
  props: PrepareWindowPresentationProps = {type: 'BOTTOM_SHEET', isCancellable: true}
): Promise<R | false> {
  const uid = ulid();
  const readyFuncName = `${PREPARE_WINDOW_PRESENTATION_FUNCTION_PREFIX}${uid}`;

  await new Promise((resolve) => {
    (window as any)[readyFuncName] = resolve;
    commonCallInterface(PREPARE_WINDOW_PRESENTATION_FUNCTION_NAME, {
      readyFuncName,
      ...props,
    });
  });

  delete (window as any)[readyFuncName];

  return new Promise((resolve) => {
    const popup = window.open(url);
    if (!popup) {
      return;
    }

    (popup as any).data = data; // Todo: qs로 넘기는게 나을지..
    (popup as any).bottomSheetClose = (result: R) => {
      (popup as any).bottomSheetClose = () => {}; // 한번만 트리거 되게끔
      resolve(result);
      popup.close();
    };
  });
}

export interface OpenPinInputProps {
  title?: string;
  length?: number;
}

export interface OpenPinInputResponse {
  uid: string;
  pin: string;
}

const OPEN_PIN_INPUT_REQUEST_FUNCTION_NAME = 'openPinInput' as const;

function openPinInput(props: OpenPinInputProps = {}) {
  if (!props.length) {
    props.length = 4;
  }

  return new Promise<string>((resolve) => {
    const uid = ulid();

    function listener(response: OpenPinInputResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.pin);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(OPEN_PIN_INPUT_REQUEST_FUNCTION_NAME, {
      ...props,
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface QrReaderProps {
  title?: string;
  descTop?: string;
  descBottom?: string;
  descButtonText?: string;
  buttonText?: string;
}

export interface QrReaderResponse {
  uid: string;
  resultType: QrReaderResultType;
  result: string;
}

export enum QrReaderResultType {
  QR_PASS = 'QR_PASS',
  QR_FAIL = 'QR_FAIL',
  DESC_BUTTON_CLICK = 'DESC_BUTTON_CLICK',
  BUTTON_CLICK = 'BUTTON_CLICK',
}

const QR_READER_REQUEST_FUNCTION_NAME = 'qrReader' as const;

function qrReader(props: QrReaderProps = {}) {
  return new Promise<QrReaderResponse>((resolve) => {
    const uid = ulid();

    function listener(response: QrReaderResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);

      // 여기는 반드시 result 가 base64 로 인코딩되어있으므로 반환
      if (response.result) {
        response.result = atob(response.result);
      }

      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(QR_READER_REQUEST_FUNCTION_NAME, {
      ...props,
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface VinReaderProps {
  title?: string;
  desc?: string;
}

export interface VinReaderResponse {
  uid: string;
  result: string;
}

const VIN_READER_REQUEST_FUNCTION_NAME = 'vinReader' as const;

function vinReader(props: VinReaderProps = {}) {
  return new Promise<string>((resolve) => {
    const uid = ulid();

    function listener(response: VinReaderResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.result);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(VIN_READER_REQUEST_FUNCTION_NAME, {
      ...props,
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface GetAddressSearchProps {
  keyword: string;
}

export interface GetAddressSearchResponse {
  uid: string;
  result: GetAddressSearchResponseResult;
}

export interface GetAddressSearchResponseResult {
  totalCount: number;
  contents: Array<GetAddressSearchResponseResultContent>;
}

export interface GetAddressSearchResponseResultContent {
  id: string;
  title: string;
  distance: number;
  distanceUnit: string;
  address: string;
  latitude: string;
  longitude: string;
  link: string;
}

const GET_ADDRESS_SEARCH_REQUEST_FUNCTION_NAME = 'getAddressSearch' as const;

function getAddressSearch(props: GetAddressSearchProps) {
  return new Promise<GetAddressSearchResponseResult>((resolve) => {
    const uid = ulid();

    function listener(response: GetAddressSearchResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.result);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_ADDRESS_SEARCH_REQUEST_FUNCTION_NAME, {
      ...props,
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface OpenNavigationExploreProps {
  title: string;
  address: string;
  latitude: string;
  longitude: string;
}

const OPEN_NAVIGATION_EXPLORE_REQUEST_FUNCTION_NAME = 'openNavigationExplore' as const;

function openNavigationExplore(props: OpenNavigationExploreProps) {
  oneAppCallInterface(OPEN_NAVIGATION_EXPLORE_REQUEST_FUNCTION_NAME, {...props});
}

export interface OpenTransferPopupProps {
  title: string;
  address: string;
  latitude: number;
  longitude: number;
}

export interface OpenTransferPopupResponse {
  uid: string;
}

const OPEN_TRANSFER_POPUP_REQUEST_FUNCTION_NAME = 'openTransferPopup' as const;

function openTransferPopup(props: OpenTransferPopupProps) {
  return new Promise<OpenTransferPopupResponse>((resolve) => {
    const uid = ulid();

    function listener(response: OpenTransferPopupResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(OPEN_TRANSFER_POPUP_REQUEST_FUNCTION_NAME, {
      ...props,
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface OpenExploreViewProps {
  /**
   * keyword 또는 filter
   */
  searchType: 'keyword' | 'filter';
  /**
   * 검색 키워드 또는 Carpay(현재는 카페이 필터만 지원)
   */
  value: string;
}

const OPEN_EXPLORE_VIEW_REQUEST_FUNCTION_NAME = 'openExploreView' as const;

function openExploreView(props: OpenExploreViewProps) {
  oneAppCallInterface(OPEN_EXPLORE_VIEW_REQUEST_FUNCTION_NAME, {...props});
}

export interface RequestLocationResponse {
  uid: string;
  latitude: number | null;
  longitude: number | null;
}

const REQUEST_LOCATION_REQUEST_FUNCTION_NAME = 'requestLocation' as const;

function requestLocation() {
  return new Promise<RequestLocationResponse>((resolve) => {
    const uid = ulid();

    function listener(response: RequestLocationResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(REQUEST_LOCATION_REQUEST_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

const REQUEST_CHARGING_STATUS_FUNCTION_NAME = 'requestChargingStatus' as const;

export interface Time {
  hour: number;
  minute: number;
}

export interface RequestChargingStatusResponse {
  uid: string;
  isCharging: boolean | null;
  remainTime: Time | null;
}

function requestChargingStatus() {
  return new Promise<RequestChargingStatusResponse>((resolve) => {
    const uid = ulid();

    function listener(response: RequestChargingStatusResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(REQUEST_CHARGING_STATUS_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

const OPEN_CHARGING_MAP_FUNCTION_NAME = 'openChargingMap' as const;

interface OpenChargingMapProps {
  providerId: string;
  stationId: string;
}

function openChargingMap(props?: OpenChargingMapProps) {
  oneAppCallInterface(OPEN_CHARGING_MAP_FUNCTION_NAME, {...props});
}

interface MakeAppointment {
  type: 'MAKE_APPOINTMENT';
  workshopId?: string;
  vin: string;
  // partCode: PartLigCdCode; // <- DI 에서는 안쓰는 인터페이스라 제거
}

export type OpenMaintenanceMapProps =
  | MakeAppointment
  | {
      type: 'CHANGE_MY_WORKSHOP';
      vin: string;
    };

export interface OpenMaintenanceMapResponse {
  uid: string;
}

const OPEN_MAINTENANCE_MAP_FUNCTION_NAME = 'openMaintenanceMap' as const;

function openMaintenanceMap(props: OpenMaintenanceMapProps) {
  return new Promise<string>((resolve) => {
    const uid = ulid();

    function listener(response: OpenMaintenanceMapResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.uid);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(OPEN_MAINTENANCE_MAP_FUNCTION_NAME, {
      ...props,
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface OnDateSelectedProps {
  date: string;
}

const ON_DATE_SELECTED_FUNCTION_NAME = 'onDateSelected' as const;

function onDateSelected(props: OnDateSelectedProps) {
  oneAppCallInterface(ON_DATE_SELECTED_FUNCTION_NAME, {...props});
}

export interface OnDateTimeSelectedProps {
  dateTime: string;
}

const ON_DATE_TIME_SELECTED_FUNCTION_NAME = 'onDateTimeSelected' as const;

function onDateTimeSelected(props: OnDateTimeSelectedProps) {
  oneAppCallInterface(ON_DATE_TIME_SELECTED_FUNCTION_NAME, {...props});
}

export interface OpenVehicleManagementProps {
  vin: string;
}

const OPEN_VEHICLE_MANAGEMENT_FUNCTION_NAME = 'openVehicleManagement' as const;

function openVehicleManagement({vin}: OpenVehicleManagementProps) {
  oneAppCallInterface(OPEN_VEHICLE_MANAGEMENT_FUNCTION_NAME, {vin});
}

export type MainTabType = 'map' | 'control' | 'feed' | 'webView';
export type IdType = MainTabType extends 'feed' ? 'home' | 'store' | 'mycar' | 'profile' : string;
export type AppScheme = 'charge' | 'maintenance' | 'availablevehicles' | `mainTab?type=${MainTabType}${`&id=${IdType}` | ''}`;
export type NextScheme = `app://${AppScheme}`;

export interface CloseContentProps {
  nextScheme?: NextScheme;
  popToRoot?: boolean;
}

const EXIT_FUNCTION_NAME = 'exit' as const;

function closeContent({nextScheme, popToRoot = false}: CloseContentProps = {}) {
  if (!nextScheme) {
    oneAppCallInterface(EXIT_FUNCTION_NAME, {nextScheme});
    return;
  }

  const [path, search] = nextScheme?.split('?');
  const params = new URLSearchParams(search);

  params.append('popToRoot', `${popToRoot}`);

  oneAppCallInterface(EXIT_FUNCTION_NAME, {nextScheme: `${path}?${params.toString()}`});
}

export interface AgreeTermsProps {
  termGroupType: string;
}

const AGREE_TERMS_FUNCTION_NAME = 'agreeTerms' as const;

function agreeTerms({termGroupType}: AgreeTermsProps) {
  oneAppCallInterface(AGREE_TERMS_FUNCTION_NAME, {termGroupType});
}

export interface VehicleRefetchProps {
  type: string;
}

const VEHICLE_REFETCH_FUNCTION_NAME = 'vehicleRefetch' as const;

function vehicleRefetch(props?: VehicleRefetchProps) {
  oneAppCallInterface(VEHICLE_REFETCH_FUNCTION_NAME, props || {type: ''});
}

export interface ShareLocationProps {
  latitude: number;
  longitude: number;
  title?: string;
  address?: string;
}

const SHARE_LOCATION_FUNCTION_NAME = 'openShareLocation' as const;

function openShareLocation(props: ShareLocationProps) {
  oneAppCallInterface(SHARE_LOCATION_FUNCTION_NAME, props);
}

export interface AuthResponse {
  uid: string;
  isAuth: boolean;
  isCanceled: boolean;
}

const AUTH_REQUEST_FUNCTION_NAME = 'authRequest' as const;

function authRequest() {
  return new Promise<AuthResponse>((resolve) => {
    const uid = ulid();

    function listener(response: AuthResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(AUTH_REQUEST_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface VehicleInitializeProps {
  vin: string;
}

export interface VehicleInitializeResponse {
  uid: string;
  isSuccess: boolean;
}

const VEHICLE_INITIALIZE_FUNCTION_NAME = 'vehicleInitialize' as const;

/**
 * @link https://hmg.dooray.com/project/pages/3762473837935308601
 */
function vehicleInitialize(props: VehicleInitializeProps) {
  return new Promise<boolean>((resolve) => {
    const uid = ulid();

    function listener(response: VehicleInitializeResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.isSuccess);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(VEHICLE_INITIALIZE_FUNCTION_NAME, {
      uid,
      vin: props.vin,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface RequestProfileManageSupportResponse {
  uid: string;
  isProfileManageSupport: boolean;
}

const REQUEST_PROFILE_MANAGE_SUPPORT_FUNCTION_NAME = 'requestProfileManageSupport' as const;

function requestProfileManageSupport() {
  return new Promise<boolean>((resolve) => {
    const uid = ulid();

    function listener(response: RequestProfileManageSupportResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.isProfileManageSupport);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(REQUEST_PROFILE_MANAGE_SUPPORT_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export enum NfcStatus {
  'ENABLED' = 'enabled',
  'DISABLED' = 'disabled',
  'UNSUPPOERTED' = 'unsupported',
}

export interface GetNfcStatusResponse {
  uid: string;
  nfcStatus: NfcStatus;
}

const GET_NFC_STATUS_FUNCTION_NAME = 'getNfcStatus' as const;

function getNfcStatus() {
  return new Promise<NfcStatus>((resolve) => {
    const uid = ulid();

    function listener(response: GetNfcStatusResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.nfcStatus);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_NFC_STATUS_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

const OPEN_NFC_SETTINGS_FUNCTION_NAME = 'openNfcSettings' as const;

function openNfcSettings() {
  oneAppCallInterface(OPEN_NFC_SETTINGS_FUNCTION_NAME);
}

export interface openNfcPaymentProps {
  title: string;
  point: number;
  cardNumber: string;
  description: string;
}

export interface openNfcPaymentResult {
  uid: string;
}

const OPEN_NFC_PAYMENT_FUNCTION_NAME = 'openNfcPayment' as const;

function openNfcPayment(props: openNfcPaymentProps) {
  oneAppCallInterface(OPEN_NFC_PAYMENT_FUNCTION_NAME, {
    title: props.title,
    point: props.point,
    cardNumber: props.cardNumber,
    description: props.description,
    resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
  });
}

export interface GetCcidResponse {
  uid: string;
  ccid: string;
}

const GET_CCID_FUNCTION_NAME = 'getCcid' as const;

function getCcid() {
  return new Promise<string>((resolve) => {
    const uid = ulid();

    function listener(response: GetCcidResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.ccid);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_CCID_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface GetFontScaleResponse {
  uid: string;
  fontScale: number;
}

const GET_FONT_SCALE_FUNCTION_NAME = 'getFontsize' as const;

function getFontScale() {
  return new Promise<GetFontScaleResponse['fontScale']>((resolve) => {
    const uid = ulid();

    function listener(response: GetFontScaleResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.fontScale);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_FONT_SCALE_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface GetDkStateResponse {
  uid: string;
  isMainVehicle: boolean;
  isRegisteredDk1: boolean;
}

const GET_DK_STATE_FUNCTION_NAME = 'getDkState' as const;

function getDkState(vin: string) {
  return new Promise<GetDkStateResponse>((resolve) => {
    const uid = ulid();

    function listener(response: GetDkStateResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_DK_STATE_FUNCTION_NAME, {
      uid,
      vin,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface GetCarWarningLightStatusResponse {
  uid: string;
  status: WarningLightStatus;
}

interface WarningLightStatus {
  lamp: InvalidOrAbnormal;
  brakeOil: InvalidOrAbnormal;
  engineOil: InvalidOrAbnormal;
  lowVoltageBattery: NormalOrWarningOrGoodOrInvalid;
  electricVehicleBattery: InvalidOrAbnormal;
  lithiumVehicleBattery: InvalidOrAbnormal;
  washer: InvalidOrAbnormal;
  fuel: InvalidOrAbnormal;
  ureaPercent: number;
  ecu: Array<EcuStatus>;
  tire: TireStatus;
}

interface EcuStatus {
  ecuId: EcuId;
  systemName: string;
  isNormal: boolean;
}

export enum EcuId {
  LAMP, //조명
  BRAKE_OIL, //브레이크 오일
  ENGINE_OIL, //엔진 오일
  WASHER, //워셔액
  TIRE_PRESSURE, //타이어 공기압, tire
  EV_BATTERY, // electricVehicleBattery
  BATTERY_FIRE_WARNING, //배터리 화재 위험 경보, lithiumVehicleBattery
  FUEL, // 연료

  VOLTAGE_BATTERY_12V_NORMAL, //12V 배터리 (보통) lowVoltageBattery
  VOLTAGE_BATTERY_12V_WARNING, //12V 배터리 (주의) lowVoltageBattery
  UREA_PERCENT, //요소수 잔여량

  MCU, //모터 제어 시스템
  BMS, //배터리 제어 시스템
  BHDC, //양방향 고전압 직류 변환 시스템
  OBC, //탑재형 완속 충전기
  EMS, //엔진 제어 시스템
  VCU, //총괄 제어기 관리 시스템
  TCU, //변속 제어 시스템
  ACU, //에어백 제어 시스템
  EPB, //전자식 주차 브레이크 시스템
  MDPS, //전동식 조향 시스템
  ADAS, //운전자 보조 시스템
  ISS, // 제동 및 차체 통합 제어 시스템 ( "ABS/EPB/ESC" )
  ECS, //전자제어 서스펜션 시스템
  TPMS, //타이어 공기압 경보 시스템
  HMU, //수소 저장 시스템
  FCU, //연료 전지 제어 시스템
  BPCU, //연료 전지 공기 압축기 제어 시스템
  LDC, //저전압 직류 변환 시스템
  HCU, //하이브리드 동력 제어 시스템
}

interface TireStatus {
  frontLeft: InvalidOrAbnormal;
  frontRight: InvalidOrAbnormal;
  rearLeft: InvalidOrAbnormal;
  rearRight: InvalidOrAbnormal;
}

type InvalidOrAbnormal = 'Invalid' | 'Abnormal';
type NormalOrWarningOrGoodOrInvalid = 'Normal' | 'Warning' | 'Good' | 'Invalid';

const GET_CAR_WARNING_LIGHT_STATUS_FUNCTION_NAME = 'getCarWarningLightStatus' as const;

function getCarWarningLightStatus() {
  return new Promise<GetCarWarningLightStatusResponse['status']>((resolve) => {
    const uid = ulid();

    function listener(response: GetCarWarningLightStatusResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.status);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_CAR_WARNING_LIGHT_STATUS_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface GetEPrivacyServiceListResponse {
  uid: string;
  vehicleServiceList: VehicleServiceListType;
}

export interface VehicleServiceListType {
  ePrivacySupported: boolean;
  serviceListAllEnabled: boolean;
  offlineModeEnabled: boolean;
}

const GET_E_PRIVACY_SERVICELIST_FUNCTION_NAME = 'getVehicleServiceList' as const;

function getVehicleServiceList() {
  return new Promise<VehicleServiceListType>((resolve) => {
    const uid = ulid();

    function listener(response: GetEPrivacyServiceListResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.vehicleServiceList);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_E_PRIVACY_SERVICELIST_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export type VideoCodec = 'h264';
export type VideoQuality = 'low' | 'medium' | 'high';
export type FileFormat = 'pdf';

interface imageType {
  limitSize: {
    width: number;
    height: number;
  };
}

interface videoType {
  resolution: number;
  codec: VideoCodec;
  frameRate: number;
  quality: VideoQuality;
  maxDuration: number;
}

interface fileType {
  types: Array<FileFormat>;
  limitSize: number;
}

export interface UploadFileParams {
  service: string;
  mediaTypes: {
    image: imageType;
    video: videoType;
    file: fileType;
  };
}

export interface UploadFileResponse {
  uid: string;
  file: {
    id: string;
    name: string;
    url: string;
  };
  thumbnail?: {
    id?: string;
    name?: string;
    url?: string;
  };
}

const UPLOAD_FILE_FUNCTION_NAME = 'fileUpload' as const;

function uploadFile(props: UploadFileParams) {
  return new Promise<UploadFileResponse>((resolve) => {
    const uid = ulid();

    function listener(response: UploadFileResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);

      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(UPLOAD_FILE_FUNCTION_NAME, {
      ...props,
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface CallExternalCertificationResponse {
  uid: string;
  isSuccess: boolean;
  errorCode?: string;
  errorMessage?: string;
}

export interface CallExternalCertificationResponse {
  uid: string;
  isSuccess: boolean;
  errorMessage?: string;
}

const CALL_EXTERNAL_CERTIFICATION_FUNCTION_NAME = 'callExternalCertification' as const;

function callExternalCertification(externalScheme: string, storeUrl: string) {
  return new Promise<CallExternalCertificationResponse>((resolve) => {
    const uid = ulid();

    function listener(response: CallExternalCertificationResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(CALL_EXTERNAL_CERTIFICATION_FUNCTION_NAME, {
      uid,
      externalScheme,
      storeUrl,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface GetHcsAuthTokenResponse {
  uid: string;
  isSuccess: boolean;
  accessToken: string;
  expiresIn: string;
}

const GET_HCS_AUTH_TOKEN_FUNCTION_NAME = 'getHcsAuthToken' as const;

function getHcsAuthToken() {
  return new Promise<GetHcsAuthTokenResponse>((resolve) => {
    const uid = ulid();

    function listener(response: GetHcsAuthTokenResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_HCS_AUTH_TOKEN_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export interface GetDeviceUUIDResponse {
  deviceUUID: string;
  uid: string;
}

export const GET_DEVICE_UUID_FUNCTION_NAME = 'getDeviceUUID' as const;

function getDeviceUUID() {
  return new Promise<string>((resolve) => {
    const uid = ulid();

    function listener(response: GetDeviceUUIDResponse) {
      if (response.uid !== uid) {
        return;
      }
      webviewEvent.removeListener(listener);
      resolve(response.deviceUUID);
    }

    webviewEvent.addListener(listener);
    oneAppCallInterface(GET_DEVICE_UUID_FUNCTION_NAME, {
      uid,
      resultFuncName: WEBVIEW_RESULT_FUNCTION_NAME,
    });
  });
}

export const ccWebviewUtils = {
  getAuthToken,
  openBottomSheetWebView,
  openBottomSheetWebViewUrl,
  openPinInput,
  qrReader,
  vinReader,
  getAddressSearch,
  openNavigationExplore,
  openTransferPopup,
  openExploreView,
  requestLocation,
  requestChargingStatus,
  openChargingMap,
  openMaintenanceMap,
  onDateSelected,
  onDateTimeSelected,
  openVehicleManagement,
  closeContent,
  agreeTerms,
  vehicleRefetch,
  openShareLocation,
  authRequest,
  vehicleInitialize,
  requestProfileManageSupport,
  getNfcStatus,
  openNfcSettings,
  openNfcPayment,
  getCcid,
  getFontScale,
  getDkState,
  getCarWarningLightStatus,
  getVehicleServiceList,
  uploadFile,
  callExternalCertification,
  getHcsAuthToken,
  getDeviceUUID,
};
